import { isEmpty } from 'utils/ObjectUtil';
import i18next from 'i18next';
import {
    ColorDarkuiAccent2,
    ColorDarkuiAccent3,
    ColorRedPrimary,
    ColorBluePrimary,
    ColorWhite,
    ColorOrangePrimary,
    ColorOrangeDark1,
    ColorYellowPrimary,
    ColorPurplePrimary,
    ColorGreenPrimary,
    ColorVioletPrimary,
    ColorLimeDark3,
    ColorLightuiAccent1,
    Color131,
    ColorBlack
} from 'ui-connect/styles/default-theme';

import TrainingStatusFeedbackMapper from './reports/TrainingStatusFeedbackMapper';
import DateParser from 'utils/date/DateParser';
import { getNumberOfDaysBetween } from 'utils/DateUtils';
import { getPrimaryTracker } from 'DailySummary/utils/DailySummaryPageUtil';
import DateFormatter from './date/DateFormatter';

var TrainingStatusUtil = {
    Statuses: {
        No_Status: 0,
        Detraining: 1,
        Unproductive: 2,
        Overreaching: 3,
        Maintaining: 4,
        Recovery: 5,
        Peaking: 6,
        Productive: 7,
        Strained: 8
    },

    STATUS_COLORS: {
        0: { backgroundColor: ColorDarkuiAccent3, color: ColorBlack }, // no status
        1: { backgroundColor: ColorDarkuiAccent3, color: ColorWhite }, // detraining
        2: { backgroundColor: ColorOrangePrimary, color: ColorWhite }, // unproductive
        3: { backgroundColor: ColorRedPrimary, color: ColorWhite }, // overreaching
        4: { backgroundColor: ColorYellowPrimary, color: ColorBlack }, // maintaining
        5: { backgroundColor: ColorBluePrimary, color: ColorWhite }, // recovery
        6: { backgroundColor: ColorPurplePrimary, color: ColorWhite }, // peaking
        7: { backgroundColor: ColorGreenPrimary, color: ColorWhite }, // productive
        8: { backgroundColor: ColorVioletPrimary, color: ColorWhite } // peaking
    },

    HeightTypes: {
        BASIC: 'BASIC',
        MAX: 'MAX'
    },

    VO2MaxCategory: {
        POOR: 'Poor',
        FAIR: 'Fair',
        GOOD: 'Good',
        EXCELLENT: 'Excellent',
        SUPERIOR: 'Superior'
    },

    Colors: {
        red: '#ff0035',
        mango: '#ff9e0d',
        green: '#50b012',
        blue: '#11a9ed',
        purple: '#cf23b8'
    },

    LoadFocusTypes: {
        NO_RESULT: {
            key: 'training.status.load.focus.no.result',
            value: 'NO_RESULT',
            phrase: 'load.focus.no.result.phrase'
        },
        INVALID: {
            key: 'TRAINING_STATUS_CURRENT_STATUS',
            value: 'NO_RESULT',
            phrase: 'load.focus.no.result.phrase'
        },
        BELOW_TARGETS: {
            key: 'training.status.load.focus.below.targets',
            value: 'BELOW_TARGETS',
            phrase: 'load.focus.below.targets.phrase'
        },
        AER_LOW_SHORT: {
            key: 'training.status.load.focus.aerobic.short.low',
            value: 'AER_LOW_SHORT',
            phrase: 'load.focus.aerobic.short.low.phrase'
        },
        AEROBIC_LOW_SHORTAGE: {
            key: 'training.status.load.focus.aerobic.short.low',
            value: 'AEROBIC_LOW_SHORTAGE',
            phrase: 'load.focus.aerobic.short.low.phrase'
        },
        AER_HIGH_SHORT: {
            key: 'training.status.load.focus.aerobic.short.high',
            value: 'AER_HIGH_SHORT',
            phrase: 'load.focus.aerobic.short.high.phrase'
        },
        AEROBIC_HIGH_SHORTAGE: {
            key: 'training.status.load.focus.aerobic.short.high',
            value: 'AEROBIC_HIGH_SHORTAGE',
            phrase: 'load.focus.aerobic.short.high.phrase'
        },
        ANAER_SHORT: {
            key: 'training.status.load.focus.anaerobic.short',
            value: 'ANAER_SHORT',
            phrase: 'load.focus.anaerobic.short.phrase'
        },
        ANAEROBIC_SHORTAGE: {
            key: 'training.status.load.focus.anaerobic.short',
            value: 'ANAEROBIC_SHORTAGE',
            phrase: 'load.focus.anaerobic.short.phrase'
        },
        BALANCED: {
            key: 'training.status.load.focus.balanced',
            value: 'BALANCED',
            phrase: 'load.focus.balanced.phrase'
        },
        AER_LOW_FOCUS: {
            key: 'training.status.load.focus.aerobic.low',
            value: 'AER_LOW_FOCUS',
            phrase: 'load.focus.aerobic.low.phrase'
        },
        AEROBIC_LOW_FOCUS: {
            key: 'training.status.load.focus.aerobic.low',
            value: 'AEROBIC_LOW_FOCUS',
            phrase: 'load.focus.aerobic.low.phrase'
        },
        AER_HIGH_FOCUS: {
            key: 'training.status.load.focus.aerobic.high',
            value: 'AER_HIGH_FOCUS',
            phrase: 'load.focus.aerobic.high.phrase'
        },
        AEROBIC_HIGH_FOCUS: {
            key: 'training.status.load.focus.aerobic.high',
            value: 'AEROBIC_HIGH_FOCUS',
            phrase: 'load.focus.aerobic.high.phrase'
        },
        ANAER_FOCUS: {
            key: 'training.status.load.focus.anaerobic',
            value: 'ANAER_FOCUS',
            phrase: 'load.focus.anaerobic.phrase'
        },
        ANAEROBIC_FOCUS: {
            key: 'training.status.load.focus.anaerobic',
            value: 'ANAEROBIC_FOCUS',
            phrase: 'load.focus.anaerobic.phrase'
        },
        ABOVE_TARGETS: {
            key: 'training.status.load.focus.above.targets',
            value: 'ABOVE_TARGETS',
            phrase: 'load.focus.above.targets.phrase'
        },
        ON_TARGET: {
            key: 'training.status.load.focus.on.target',
            value: 'ON_TARGET',
            phrase: 'load.focus.on.target.phrase'
        },
        LOW_AEROBIC_OVERAGE: {
            key: 'training.status.load.focus.low.aerobic.overage',
            value: 'LOW_AEROBIC_OVERAGE',
            phrase: 'load.focus.low.aerobic.overage.phrase'
        },
        HIGH_AEROBIC_OVERAGE: {
            key: 'training.status.load.focus.high.aerobic.overage',
            value: 'HIGH_AEROBIC_OVERAGE',
            phrase: 'load.focus.high.aerobic.overage.phrase'
        },
        ANAEROBIC_OVERAGE: {
            key: 'training.status.load.focus.anaerobic.overage',
            value: 'ANAEROBIC_OVERAGE',
            phrase: 'load.focus.anaerobic.overage.phrase'
        }
    },

    AcclimationCategory: {
        DEACCLIMATIZING: {
            altitude: 'altitude.acclimation.trending.down.help',
            heat: 'heat.acclimation.trending.down.help'
        },
        ACCLIMATIZING: { altitude: 'altitude.acclimation.trending.up.help', heat: 'heat.acclimation.trending.up.help' },
        ACCLIMATIZED: { altitude: 'altitude.acclimation.maintaining.help', heat: 'heat.acclimation.trending.up.help' }
    },

    SportTypes: {
        Cycling: { value: 'CYCLING', key: 'training.status.sport.type.cycling' },
        Running: { value: 'RUNNING', key: 'training.status.sport.type.running' }
    },

    AcclimationType: {
        Altitude: 'Altitude',
        Heat: 'Heat'
    },

    ActivityTypes: {
        cycling: { value: 'exercise.load.activity.ride' },
        running: { value: 'exercise.load.activity.run' },
        swimming: { value: 'exercise.load.activity.swim' }
    },

    //constants - will be inserted into placeholder strings
    TrainingMetrics: {
        basicTrainingHeightInFeet: '2,600',
        basicTrainingHeightInMeters: '800',
        maxAltitudeAcclimationHeightFeet: '13,000',
        maxAltitudeAcclimationHeightMeters: '4,000',
        temperatureFahrenheit: '72',
        temperatureCelsius: '22'
    },

    Trend: {
        Fitness: 'Fitness',
        Load: 'Load'
    },

    FitnessLoadTrend: {
        1: 'trendDecreaseIcon',
        2: '',
        3: 'trendIncreaseIcon'
    },

    AcuteTrainingLoadSeries: {
        OPTIMAL_RANGE: {
            defaultColor: ColorLimeDark3,
            color: ColorLightuiAccent1,
            name: 'optimal'
        },
        ACUTE_LOAD: {
            defaultColor: Color131,
            color: ColorDarkuiAccent2,
            name: 'acute_training_load'
        },
        CHRONIC_LOAD: {
            color: ColorOrangeDark1,
            name: 'chronic_load'
        },
        LOAD_RATIO: {
            color: Color131,
            name: 'load_ratio'
        }
    },

    isTrainingStatusAvailable: function(usageIndicators) {
        return (
            usageIndicators &&
            usageIndicators.hasCurrentOrHistoricalIndicator('device', 'hasTrainingStatusCapableDevice')
        );
    },

    determineIfNoData: function(statusData) {
        var noData = true;

        if (!isEmpty(statusData)) {
            //check to see if statusData has a training status of No_Status for entire period
            for (var i = 0; i < statusData.length; i++) {
                //no status is 0 or 255
                if (statusData[i].trainingStatus !== this.Statuses.No_Status && statusData[i].trainingStatus !== 255) {
                    noData = false;
                    break;
                }
            }
        }
        return noData;
    },

    getStatusClass: function(status) {
        var statusClass;

        if (status === this.Statuses.Productive) {
            statusClass = 'training-status-productive';
        } else if (status === this.Statuses.Peaking) {
            statusClass = 'training-status-peaking';
        } else if (status === this.Statuses.Recovery) {
            statusClass = 'training-status-recovery';
        } else if (status === this.Statuses.Maintaining) {
            statusClass = 'training-status-maintaining';
        } else if (status === this.Statuses.Overreaching) {
            statusClass = 'training-status-overreaching';
        } else if (status === this.Statuses.Unproductive) {
            statusClass = 'training-status-unproductive';
        } else if (status === this.Statuses.Detraining) {
            statusClass = 'training-status-detraining';
        } else if (status === this.Statuses.Strained) {
            statusClass = 'training-status-strained';
        } else {
            statusClass = 'training-status-no-status';
        }

        return statusClass;
    },

    getStatusColor: function(status) {
        return this.STATUS_COLORS[status] || this.STATUS_COLORS[0];
    },

    getStatusNameKey(status) {
        let statusNameKey;

        if (status === this.Statuses.Productive) {
            statusNameKey = 'TRAINING_STATUS_PRODUCTIVE';
        } else if (status === this.Statuses.Peaking) {
            statusNameKey = 'TRAINING_STATUS_PEAKING';
        } else if (status === this.Statuses.Recovery) {
            statusNameKey = 'TRAINING_STATUS_RECOVERY';
        } else if (status === this.Statuses.Maintaining) {
            statusNameKey = 'TRAINING_STATUS_MAINTAINING';
        } else if (status === this.Statuses.Overreaching) {
            statusNameKey = 'TRAINING_STATUS_OVERREACHING';
        } else if (status === this.Statuses.Unproductive) {
            statusNameKey = 'TRAINING_STATUS_UNPRODUCTIVE';
        } else if (status === this.Statuses.Detraining) {
            statusNameKey = 'TRAINING_STATUS_DETRAINING';
        } else if (status === this.Statuses.Strained) {
            statusNameKey = 'TRAINING_STATUS_STRAINED';
        } else {
            statusNameKey = 'TRAINING_STATUS_NO_STATUS';
        }

        return statusNameKey;
    },

    getStatusNameKeyWithNamespace(status) {
        const statusNameKey = this.getStatusNameKey(status);
        return `reportswidget:${statusNameKey}`;
    },

    // This function uses the i18next library and might cause
    // translation async/loading issues on React pages. (It is only being
    // kept because it is also used on Backbone pages.) In React, use the
    // getStatusNameKey function above instead of this one and then
    // translate the status key using the t prop from withTranslation
    // or the Translate component.
    getStatusName: function(status) {
        const statusNameKey = this.getStatusNameKeyWithNamespace(status);
        const statusName = i18next.t(statusNameKey);
        return statusName;
    },

    getMaxStatusValue: function() {
        return Math.max(...Object.values(this.Statuses));
    },

    getStatusDescription: function(status, strainedPhrase) {
        var statusDescription;

        if (status === this.Statuses.Productive) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_PRODUCTIVE_DESCRIPTION');
        } else if (status === this.Statuses.Peaking) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_PEAKING_DESCRIPTION');
        } else if (status === this.Statuses.Recovery) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_RECOVERY_DESCRIPTION');
        } else if (status === this.Statuses.Maintaining) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_MAINTAINING_DESCRIPTION');
        } else if (status === this.Statuses.Overreaching) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_OVERREACHING_DESCRIPTION');
        } else if (status === this.Statuses.Unproductive) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_UNPRODUCTIVE_DESCRIPTION');
        } else if (status === this.Statuses.Detraining) {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_DETRAINING_DESCRIPTION');
        } else if (status === this.Statuses.Strained) {
            statusDescription = this.getStrainedDescription(strainedPhrase);
        } else {
            statusDescription = i18next.t('reportswidget:TRAINING_STATUS_NO_STATUS_DESCRIPTION');
        }

        return statusDescription;
    },

    getStrainedDescription: function(strainedPhrase) {
        return TrainingStatusFeedbackMapper.getFeedbackPhrase(this.Statuses.Strained, null, strainedPhrase);
    },

    getLoadStatus: function(loadMin, loadMax, trainingLoad) {
        var loadStatus = {
            class: null,
            tooltip: null
        };

        if (trainingLoad > loadMax) {
            loadStatus.class = 'training-load-high';
            loadStatus.tooltip = i18next.t('reportswidget:TRAINING_LOAD_HIGH');
        } else if (trainingLoad < loadMin) {
            loadStatus.class = 'training-load-low';
            loadStatus.tooltip = i18next.t('reportswidget:TRAINING_LOAD_LOW');
        } else {
            loadStatus.class = 'training-load-optimal';
            loadStatus.tooltip = i18next.t('reportswidget:TRAINING_LOAD_OPTIMAL');
        }

        return loadStatus;
    },

    shouldShowDevicePicker: function(length) {
        return length > 1;
    },

    addMetricToDataset: function(date, categories, userPreferences) {
        var dataIndex = this.calculateWeeklyDataIndex(date, categories, userPreferences);
        return dataIndex;
    },

    calculateWeeklyDataIndex: function(inputStr, categories, userPreferences) {
        var aDate = inputStr.split('-');
        var formatDate = new Date(aDate[0], aDate[1] - 1, aDate[2], 0, 0, 0);

        var mm = formatDate.getMonth() + 1; // getMonth() is zero-based
        var dd = formatDate.getDate();

        var datePref = userPreferences.get('dateFormat');
        var category = '';
        if (datePref.formatKey === 'mmddyyyy' || datePref.formatKey === 'yyyymmdd') {
            category = i18next.t('common:month.abbr.' + (mm > 9 ? mm : '0' + mm)) + ' ' + dd;
        } else {
            category = dd + ' ' + i18next.t('common:month.abbr.' + (mm > 9 ? mm : '0' + mm));
        }

        var dataIndex = categories.indexOf(category);

        // In the event that the dataset grows, we only care about those dates that are in the last year.
        if (dataIndex < 0) {
            for (var i = 0; i < categories.length; i++) {
                if (Date.parse(category) - Date.parse(categories[i]) < 0) {
                    dataIndex = i - 1;
                    break;
                }
            }
            if (dataIndex < 0) {
                dataIndex = categories.length - 1;
            }
        }
        return dataIndex;
    },

    getMetricDataIndexFromCategoryKeys: function(dateString, categoryKeys) {
        const dateStringSplited = dateString.split('-');
        const date = new Date(dateStringSplited[0], +dateStringSplited[1] - 1, dateStringSplited[2], 0, 0, 0);

        const categoryKey = DateFormatter.formatISODate(date);

        let dataIndex = categoryKeys.indexOf(categoryKey);

        if (dataIndex < 0) {
            for (var i = 0; i < categoryKeys.length; i++) {
                if (Date.parse(categoryKey) - Date.parse(categoryKeys[i]) < 0) {
                    dataIndex = i - 1;
                    break;
                }
            }
            if (dataIndex < 0) {
                dataIndex = categoryKeys.length - 1;
            }
        }
        return dataIndex;
    },

    getStatusDataForDevice: function(selectedDevice, statusData) {
        var deviceData;

        if (statusData?.latestTrainingStatusData) {
            deviceData = statusData.latestTrainingStatusData[selectedDevice];
        } else {
            return null;
        }

        return deviceData;
    },

    mapVO2MaxColor: function(category) {
        switch (category) {
            case this.VO2MaxCategory.POOR:
                return this.Colors.red;
            case this.VO2MaxCategory.FAIR:
                return this.Colors.mango;
            case this.VO2MaxCategory.GOOD:
                return this.Colors.green;
            case this.VO2MaxCategory.EXCELLENT:
                return this.Colors.blue;
            default:
                return this.Colors.purple;
        }
    },

    getLoadFocusType: function(feedbackPhrase) {
        //feedbackPhrase is a combination of TS and Load Focus, example - DETRAINING_AER_LOW_SHORT. Find the right focus type from the feedback phrase.
        let focusType = 'NO_RESULT';
        if (feedbackPhrase) {
            focusType = Object.keys(this.LoadFocusTypes).find((focusType) => {
                return feedbackPhrase.indexOf(focusType) > -1;
            });
        }
        return focusType;
    },

    getLoadFocusKey: function(feedbackPhrase) {
        const focusType = this.getLoadFocusType(feedbackPhrase);
        return this.LoadFocusTypes[focusType].key;
    },

    // This function uses the i18next library and might cause
    // translation async/loading issues on React pages. (It is only being
    // kept because it is also used on Backbone pages.) In React, use the
    // getLoadFocusKey function above instead of this one and then
    // translate the status key using the t prop from withTranslation
    // or the Translate component.
    getLoadFocus: function(feedbackPhrase, noTranslate) {
        const focusType = this.getLoadFocusType(feedbackPhrase);
        return noTranslate ? focusType : i18next.t(`reportswidget:${this.LoadFocusTypes[focusType].key}`);
    },

    getLoadFocusPhrase: function(balanceFeedbackPhrase) {
        return this.LoadFocusTypes[balanceFeedbackPhrase] ? this.LoadFocusTypes[balanceFeedbackPhrase].phrase : '';
    },

    getFriendlyActivityName: function(activityType) {
        let exerciseLoadActivityType = this.ActivityTypes[activityType];
        return exerciseLoadActivityType
            ? i18next.t(`reportswidget:${exerciseLoadActivityType.value}`)
            : i18next.t('reportswidget:exercise.load.activity.generic');
    },

    isProvisionalData(hasPTD, latestTrainingStatusData, compareDate = new Date()) {
        let provisional = false;
        if (hasPTD) {
            if (!latestTrainingStatusData.lastPrimarySyncDate) {
                provisional = true;
            } else {
                const lastSyncDate = DateParser.parseISO(latestTrainingStatusData.lastPrimarySyncDate);
                const days = getNumberOfDaysBetween(lastSyncDate, compareDate);
                // if lastsyncdate is older then 28 days than it will not be provisional data
                if (days <= 28) {
                    provisional = days !== 0;
                }
            }
        }
        return provisional;
    },

    ACUTE_LOAD_FEEDBACK_PHRASE: {
        FEEDBACK_1: 'ts_acute_load_feedback_1',
        FEEDBACK_2: 'ts_acute_load_feedback_2',
        FEEDBACK_3: 'ts_acute_load_feedback_3',
        FEEDBACK_4: 'ts_acute_load_feedback_4',
        FEEDBACK_5: 'ts_acute_load_feedback_5'
    },

    hasMultipleDevicesData(mostRecentTrainingStatus) {
        return (
            mostRecentTrainingStatus &&
            mostRecentTrainingStatus.recordedDevices &&
            mostRecentTrainingStatus.recordedDevices.length > 1
        );
    },

    getTrainingStatusColor(latestTSData) {
        const statusColorConfig = TrainingStatusUtil.getStatusColor(latestTSData?.trainingStatus);
        return { backgroundColor: statusColorConfig.backgroundColor, color: statusColorConfig.color };
    },

    getPrimaryTrackerDeviceId(deviceList) {
        return getPrimaryTracker(deviceList) && getPrimaryTracker(deviceList).deviceId;
    }
};

export const TSChartUtil = {
    getTitle: (title = '') => ({
        align: 'left',
        style: {
            fontWeight: 'normal'
        },
        text: `<div class='ts-chart-header data-bit'>${title}</div>`,
        useHTML: true,
        x: 0,
        y: 25
    }),
    getSubtitle: (subtitle = '', { x = -5 } = {}) => ({
        align: 'right',
        floating: true,
        style: {
            fontSize: '1em'
        },
        text: subtitle,
        useHTML: true,
        verticalAlign: 'top',
        x: x,
        y: 20
    })
};

export default TrainingStatusUtil;
